import { useNotify, useRedirect } from 'react-admin'
import { FileUploadWithSlideoutPreview } from 'shared/components/FileUpload/FileUploadWithSlideoutPreview'
import { toCamelCase } from 'shared/helpers/utils'
import { CoreDocumentAttachment } from 'shared/models/document'
import { breakpoints } from 'shared/theme/breakpoints'
import styled from 'styled-components'

import { useTeam } from 'helpers/useTeam'
import { captureExceptionIfProduction } from 'helpers/utils'
import { addEquipmentInspectionReport, updateEquipmentInspectionReport } from '../../api/core'
import { EquipmentType } from '../../helpers/constants'
import { EquipmentInspectionReportForm } from './EquipmentForm'

type EquipmentInspectionReportButtonProps = {
  match?: any
}

export function EquipmentInspectionReportButton({ match }: EquipmentInspectionReportButtonProps) {
  const { teamId, equipmentType: equipmentTypeParam, equipmentId } = match.params
  const equipmentType = equipmentTypeParam.toUpperCase()
  const team = useTeam(teamId)
  // TODO: @public-console-sidebar - add typing to these: equipment, inspectionRecord
  const equipment: any =
    equipmentType === EquipmentType.Truck
      ? team?.trucks.find((e: any) => e.id === equipmentId)
      : team?.trailers.find((e: any) => e.id === equipmentId)
  const inspectionRecord =
    equipmentType === EquipmentType.Truck
      ? equipment?.truck_inspection_report
      : equipment?.trailer_inspection_report
  const notify = useNotify()
  const redirectTo = useRedirect()

  const props = {
    basePath: `/teams/${teamId}/show/equipment`,
    resource: 'equipment-inspection-report',
    id: inspectionRecord?.id,
  }

  const isCreate = !inspectionRecord?.inspection_report
  const isTruck = equipmentType === EquipmentType.Truck
  const equipmentTypeString = isTruck ? 'truck' : 'trailer'

  const equipmentString = (isTruck ? 'Truck ' : 'Trailer ') + 'Inspection Data (Brakes and Tires)'

  const handleSubmit = async (values: any) => {
    if (isTruck) {
      values.truck = equipmentId
    } else {
      values.trailer = equipmentId
    }
    let equipmentInspectionReportRequest = isCreate
      ? addEquipmentInspectionReport(equipmentTypeString, values)
      : updateEquipmentInspectionReport(inspectionRecord.id, equipmentTypeString, values)

    equipmentInspectionReportRequest
      .then(() => {
        notify(`${equipmentString} updated!`, { type: 'success' })
        redirectTo(props.basePath)
      })
      .catch((error) => {
        const error_data = error.response.data
        notify(JSON.stringify(error_data), { type: 'error' })
        captureExceptionIfProduction(error)
      })
  }

  let initialValues = null
  if (!isCreate) {
    initialValues = inspectionRecord
  }

  if (!team || !equipment || !inspectionRecord) {
    return null
  }

  return (
    <GridWrap>
      <EquipmentInspectionReportForm
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        basePath={props.basePath}
        redirectTo={redirectTo}
        isTruck={isTruck}
      />
      {equipment?.last_dot_inspection_document?.document_attachments &&
        equipment?.last_dot_inspection_document?.document_attachments.length > 0 && (
          <FileUploadWithSlideoutPreview
            file={
              toCamelCase(
                equipment?.last_dot_inspection_document?.document_attachments[
                  equipment?.last_dot_inspection_document?.document_attachments.length - 1
                ]
              ) as CoreDocumentAttachment<any>
            }
            setFile={() => {}} // This is a workaround to essentially make the file read-only
            positionExpandedUnderToolbar
            showTrashIcon={false}
          />
        )}
    </GridWrap>
  )
}

const GridWrap = styled.div`
  display: grid;
  grid-template-columns: 650px 1fr;
  gap: 80px;

  @media ${breakpoints.sm} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`
